import React, { useCallback, useContext, useMemo } from "react";
import {
  useClientTable,
  Text,
  EnhancedTable,
} from "@pushpress/shared-components";
import { DataTableProps } from "@pushpress/shared-components/build/components/data/DataTable";
import { AppContext } from "../../../context/app";
import StaffCard from "../../../components/cards/StaffCard";
import {
  filterAppointments,
  getAppointmentFilterTagsMap,
} from "./AppointmentCredits.utils";
import AppointmentsFilter from "./AppointmentsFilter";
import { AppointmentsFilterValue } from "./AppointmentCredits.types";
import AppointmentStatusChip from "../AppointmentStatusChip/AppointmentStatusChip";
import DateTimeStartEndCell from "../../../components/table/DateTimeStartEndCell";
import { useAppointments } from "../AppointmentsProvider/AppointmentsProvider";
import { HistoryFragment } from "./AppointmentCredits-generated.hooks";

interface AppointmentsTableProps<K extends string>
  extends Pick<
    DataTableProps<HistoryFragment, K>,
    "data" | "renderRowMenu" | "loading"
  > {
  data: HistoryFragment[];
  onRefetch: VoidFunction;
}

function AppointmentsTable<K extends string>(props: AppointmentsTableProps<K>) {
  const { data: dataProp, onRefetch, ...other } = props;

  const app = useContext(AppContext);

  const { showScheduleModal } = useAppointments();

  const handleRowClick = (row: HistoryFragment) => {
    showScheduleModal(row.uuid, {
      onSuccess: onRefetch,
    });
  };

  const timezone = app.client!.timezone;

  const columns = useMemo(
    () => [
      {
        header: "Date",
        key: "startTime",
        isSortable: true,
        cell: (row: HistoryFragment) => (
          <DateTimeStartEndCell
            startTime={row.startTime}
            endTime={row.endTime}
            timezone={timezone}
          />
        ),
      },
      {
        key: "appointmentType",
        header: "Type",
        isSortable: true,
        hideOnMobile: true,
        width: "25%",
        sort: (a: HistoryFragment, b: HistoryFragment) =>
          a?.appointmentType?.name?.localeCompare(b?.appointmentType?.name),
        cell: (row: HistoryFragment) => {
          return (
            <>
              <Text variant="body-lg" color="wolf-800">
                {row?.appointmentType?.name}
              </Text>
              <Text variant="body-lg" color="wolf-600">
                {`${row?.appointmentType?.duration / 60} min`}
              </Text>
            </>
          );
        },
      },
      {
        key: "staff",
        header: "Staff",
        isSortable: true,
        hideOnMobile: true,
        width: "25%",
        sort: (a: HistoryFragment, b: HistoryFragment) =>
          a?.staff?.firstName?.localeCompare(b?.staff?.firstName),
        cell: (row: HistoryFragment) => {
          if (!row?.staff?.firstName && !row?.staff?.lastName) {
            return "- -";
          }
          return <StaffCard staff={row.staff} />;
        },
      },
      {
        header: "Status",
        key: "eventStatus",
        hideOnMobile: true,
        isSortable: true,
        width: "25%",
        cell: (row: HistoryFragment) => <AppointmentStatusChip data={row} />,
      },
    ],
    [timezone],
  );

  const {
    data,
    sort,
    dateRange,
    filter,
    viewSaved,
    page,
    pageCount,
    filterTagOptions,
    viewLoading,
    handleSort,
    handleDateRangeChange,
    handleFilterChange,
    handlePageChange,
    handleViewSave,
    handleFilterTagDelete,
  } = useClientTable({
    viewSaveId: "appointments-credits-filter",
    data: dataProp || [],
    columns,
    defaultSort: { key: "startTime", order: "asc" },
    defaultDateRange: "next-30-days",
    defaultRowsPerPage: 20,
    timezone: app.client!.timezone,
    filterData: filterAppointments,
    getFilterTagsMap: getAppointmentFilterTagsMap,
  });

  const renderFilter = useCallback(
    (
      filter: AppointmentsFilterValue,
      handleFilterChange: (filter: AppointmentsFilterValue) => void,
      handleClear: VoidFunction,
    ) => {
      return (
        <AppointmentsFilter
          filter={filter}
          onFilterChange={handleFilterChange}
          onClear={handleClear}
        />
      );
    },
    [],
  );

  const getRowId = useCallback((row) => {
    return row.uuid;
  }, []);

  return (
    <EnhancedTable
      {...other}
      aria-label="Appointments table"
      columns={columns}
      data={data}
      dateRange={dateRange}
      filter={filter}
      filterTagOptions={filterTagOptions}
      page={page}
      pageCount={pageCount}
      sort={sort}
      viewSaved={viewSaved}
      viewLoading={viewLoading}
      getRowId={getRowId}
      renderFilter={renderFilter}
      onDateRangeChange={handleDateRangeChange}
      onFilterChange={handleFilterChange}
      onFilterTagDelete={handleFilterTagDelete}
      onPageChange={handlePageChange}
      onSort={handleSort}
      onViewSave={handleViewSave}
      onRowClick={handleRowClick}
    />
  );
}

export default AppointmentsTable;
